/**
 * Router Component Preloader
 * 路由组件预加载器 - 确保页面刷新时组件已就绪
 */

import componentLoader, { ensureComponentsLoaded } from '@/utils/componentLoader'
import store from '@/store'

/**
 * 路由守卫 - 确保组件加载
 */
export const componentPreloadGuard = async (to, from, next) => {
  // 检查是否需要预加载组件
  const requiredComponents = getRequiredComponentsForRoute(to)

  if (requiredComponents.length === 0) {
    next()
    return
  }

  // 显示加载状态
  store.commit('showLoading')

  try {
    // 等待必要组件加载
    await componentLoader.waitForComponents(requiredComponents)

    // 标记组件已加载到 store
    requiredComponents.forEach(comp => {
      store.commit('ADD_LOADED_COMPONENT', comp)
    })

    store.commit('closeLoading')
    next()
  } catch (error) {
    console.error('Component preload failed:', error)
    store.commit('closeLoading')

    // 对于关键路由，尝试强制加载所有组件
    if (isCriticalRoute(to.path)) {
      try {
        await forceLoadCriticalComponents()
        next()
      } catch (retryError) {
        console.error('Force load failed:', retryError)
        // 可以重定向到错误页面或继续
        next()
      }
    } else {
      next()
    }
  }
}

/**
 * 判断是否为关键路由
 */
function isCriticalRoute(path) {
  const criticalPaths = [
    'cart',
    'payment',
    'checkout',
    'pay',
    'product-detail',
    'contact-details',
    'card-detail'
  ]

  return criticalPaths.some(p => path.includes(p))
}

/**
 * 获取路由所需的组件
 */
function getRequiredComponentsForRoute(route) {
  const path = route.path
  const components = []

  // 所有页面基础组件
  if (!componentLoader.loadedComponents.has('vant-base')) {
    components.push('vant-base')
  }

  if (!componentLoader.loadedComponents.has('global-base')) {
    components.push('global-base')
  }

  // 根据路由判断需要的组件
  if (path.includes('cart') || path.includes('payment') || path.includes('checkout')) {
    if (!componentLoader.loadedComponents.has('vant-form')) {
      components.push('vant-form')
    }
    if (!componentLoader.loadedComponents.has('global-popup')) {
      components.push('global-popup')
    }
  }

  // Payment page specific requirements
  if (path.includes('payment') || path.includes('pay')) {
    // Payment page uses Muse UI dialog and appbar
    if (!componentLoader.loadedComponents.has('muse-ui')) {
      components.push('muse-ui')
    }
    // Also ensure Vant base components are loaded for buttons
    if (!componentLoader.loadedComponents.has('vant-base')) {
      components.push('vant-base')
    }
    // Ensure all popup components are loaded
    if (!componentLoader.loadedComponents.has('global-popup')) {
      components.push('global-popup')
    }
  }

  // Card detail page also uses Muse UI for v-loading directive
  if (path.includes('card-detail')) {
    if (!componentLoader.loadedComponents.has('muse-ui')) {
      components.push('muse-ui')
    }
    if (!componentLoader.loadedComponents.has('global-popup')) {
      components.push('global-popup')
    }
  }

  if (path.includes('product-detail')) {
    if (!componentLoader.loadedComponents.has('vant-tabs')) {
      components.push('vant-tabs')
    }
    if (!componentLoader.loadedComponents.has('global-popup')) {
      components.push('global-popup')
    }
  }

  if (path.includes('venue-home')) {
    if (!componentLoader.loadedComponents.has('vant-tabs')) {
      components.push('vant-tabs')
    }
  }

  // contact-details 页面需要 global-popup
  if (path.includes('contact-details') || path.includes('contact_details')) {
    if (!componentLoader.loadedComponents.has('global-popup')) {
      components.push('global-popup')
    }
  }

  // 表单相关页面需要 Element UI
  if (path.includes('login') || path.includes('register') || path.includes('form')) {
    if (!componentLoader.loadedComponents.has('element-ui')) {
      components.push('element-ui')
    }
  }

  // 可能需要 Muse UI 的页面
  if (path.includes('dialog') || path.includes('select') || path.includes('app')) {
    if (!componentLoader.loadedComponents.has('muse-ui')) {
      components.push('muse-ui')
    }
  }

  // 支付相关页面需要 UI 库
  if (path.includes('payment') || path.includes('checkout') || path.includes('pay')) {
    if (!componentLoader.loadedComponents.has('element-ui')) {
      components.push('element-ui')
    }
    if (!componentLoader.loadedComponents.has('muse-ui')) {
      components.push('muse-ui')
    }
  }

  return components
}

/**
 * 强制加载关键组件
 */
async function forceLoadCriticalComponents() {
  console.log('Force loading critical components...')

  // 立即加载 Vant 组件
  if (!window.vantComponentsLoaded) {
    await import('@/plugins/vant').then(module => {
      if (module.loadVantComponents) {
        return module.loadVantComponents()
      }
    })
  }

  // 立即加载全局组件
  if (!window.globalComponentsLoaded) {
    await import('@/components/global').then(() => {
      window.globalComponentsLoaded = true
    })
  }

  // 立即加载 UI 库组件
  const mainModule = await import('@/main')

  // 加载 Muse UI
  if (!componentLoader.loadedComponents.has('muse-ui') && mainModule.loadMuseUIComponents) {
    await mainModule.loadMuseUIComponents()
  }

  // 加载 Element UI
  if (!componentLoader.loadedComponents.has('element-ui') && mainModule.loadElementUIComponents) {
    await mainModule.loadElementUIComponents()
  }
}

/**
 * 预加载下一个可能访问的路由组件
 */
export function preloadNextRouteComponents(currentRoute) {
  // 根据当前路由预测下一个可能的路由
  const nextPossibleComponents = []

  if (currentRoute.path.includes('venue-home')) {
    nextPossibleComponents.push('vant-form', 'global-popup')
  }

  if (currentRoute.path.includes('product-detail')) {
    nextPossibleComponents.push('vant-form')
  }

  // 异步预加载，不阻塞当前操作
  if (nextPossibleComponents.length > 0) {
    setTimeout(() => {
      componentLoader.waitForComponents(nextPossibleComponents).catch(err => {
        console.warn('Preload next route components failed:', err)
      })
    }, 1000)
  }
}

/**
 * Vue Router 插件
 */
export default {
  install(router) {
    // 添加全局前置守卫
    router.beforeEach(componentPreloadGuard)

    // 添加全局后置钩子用于预加载
    router.afterEach((to, from) => {
      preloadNextRouteComponents(to)
    })
  }
}
